import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Play, SearchX } from 'lucide-react';
import Button from '@/components/primitives/Button.jsx';
import Icon from '@/components/primitives/Icon.jsx';
import EmptyState from '@/components/patterns/EmptyState.jsx';
import PageHeader from '@/components/patterns/PageHeader.jsx';
import Tabs from '@/components/patterns/Tabs.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import { automationListItem, automationRuns, automationScheduleDetail, automationTriggerDetail, automationWorkflowDetail } from '@/redux/selectors/pieveSelectors/automationCatalogSelectors.js';
import AUTOMATION_KINDS from '../lib/AUTOMATION_KINDS.js';
import buildAutomationPathNodes from '../lib/buildAutomationPathNodes.js';
import resolveRunExecutionState from '../lib/resolveRunExecutionState.js';
import runAutomationNow from '../lib/runAutomationNow.js';
import AskPieveDialog from '../components/AskPieveDialog.jsx';
import AutomationActiveSwitch from '../components/AutomationActiveSwitch.jsx';
import AutomationDetailStateHandler from '../components/AutomationDetailStateHandler.jsx';
import AutomationPath from '../components/AutomationPath.jsx';
import AutomationRunHistory from '../components/AutomationRunHistory.jsx';

const RUN_NOW_LABEL = { schedules: 'Run now', triggers: 'Check now', workflows: 'Run now' };
const WATCH_AFTER_RUN_MS = 60000;

/** One automation: how it works in plain steps, its runs with what each did, and its controls. A link with `?run=`
   (from a workflow email) opens run history on that run. */
const AutomationDetailPage = ({ kind, id }) => {
  const automation = useSelector((state) => automationListItem(state, kind, id));
  const schedule = useSelector((state) => automationScheduleDetail(state, id));
  const trigger = useSelector((state) => automationTriggerDetail(state, id));
  const workflow = useSelector((state) => automationWorkflowDetail(state, id));
  const runs = useSelector((state) => automationRuns(state, kind, id));
  const [asking, setAsking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [watchingAfterRun, setWatchingAfterRun] = useState(false);
  const [searchParams] = useSearchParams();
  const linkedRunId = searchParams.get('run');
  const [tab, setTab] = useState(linkedRunId ? 'runs' : 'steps');
  const hasLiveRun = runs.some((run) => ['running', 'queued'].includes(resolveRunExecutionState(run.status).state));
  const watch = hasLiveRun || watchingAfterRun;
  const kindInfo = AUTOMATION_KINDS[kind];

  const handleRunNow = async () => {
    setStarting(true);
    const result = await runAutomationNow({ kind, id });
    setStarting(false);

    if (result?.success === false) {
      toastManager.add({ title: 'The run did not start', description: result.message || 'Try again in a moment.', type: 'error' });
      return;
    }

    toastManager.add({ title: kind === 'triggers' ? 'Checking now' : 'Run started', description: 'It shows up in run history in a few seconds.', type: 'success' });
    setWatchingAfterRun(true);
    setTimeout(() => setWatchingAfterRun(false), WATCH_AFTER_RUN_MS);
    setTab('runs');
  };

  const backLink = (
    <Link className="automations-detail__back" to="/automations">
      <Icon icon={ArrowLeft} size="sm" />
      Automations
    </Link>
  );

  if (!automation) {
    return (
      <div className="comp-page">
        {backLink}
        <EmptyState icon={SearchX} title="Automation not found" description="It may have been removed, or it belongs to another organization." />
      </div>
    );
  }

  const nodes = buildAutomationPathNodes({ kind, schedule, trigger, workflow });

  return (
    <div className="comp-page">
      <AutomationDetailStateHandler kind={kind} id={id} watch={watch} />
      {backLink}
      <PageHeader
        title={automation.name}
        description={automation.nextRun ? `${kindInfo.singular}. ${automation.when}. Next run ${automation.nextRun}.` : `${kindInfo.singular}. ${automation.when}`}
        actions={
          <div className="automations-detail__actions">
            <AutomationActiveSwitch automation={automation} />
            <Button iconStart={Play} loading={starting} onClick={handleRunNow}>
              {RUN_NOW_LABEL[kind]}
            </Button>
            <Button variant="primary" iconStart={MessageSquare} onClick={() => setAsking(true)}>
              Ask Pieve to change it
            </Button>
          </div>
        }
      />
      <Tabs
        label="Automation"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'steps', label: 'How it works', panel: nodes.length > 0 ? <AutomationPath nodes={nodes} /> : <p className="automations-detail__note">Loading its steps...</p> },
          { value: 'runs', label: runs.length > 0 ? `Run history (${runs.length})` : 'Run history', panel: <AutomationRunHistory runs={runs} workflowId={kind === 'workflows' ? id : null} linkedRunId={linkedRunId} /> }
        ]}
      />
      <AskPieveDialog open={asking} onOpenChange={setAsking} automation={automation} />
    </div>
  );
};

export default AutomationDetailPage;
